import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import { IconFilter, IconRefresh, IconMapPin } from '@tabler/icons-react';

import TopVerifiedDrugs from './TopVerifiedDrugs';
import ActiveProvidersThisWeek from './ActiveProvidersThisWeek';
import VerificationStatsCards from './VerificationStatsCards';
import ActivelyQueriedPlans from './ActivelyQueriedPlans';
import VerificationsByState from './VerificationsByState';
import superAdminApiService from '../../../services/super-admin-api.service';

const CoverageVerificationModuleUsage = () => {
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [filters, setFilters] = useState({});
  const [organizations, setOrganizations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState(null);

  const baseRoles = [
    { id: 'all', name: 'All Roles' },
    { id: 'provider', name: 'Provider' },
    { id: 'support', name: 'Support Staff' },
  ];

  useEffect(() => {
    const fetchOrganizations = async () => {
      setOrgError(false);
      setOrgLoading(true);

      try {
        const data = await superAdminApiService.listOrganizationUsers();
        const filteredData = data.results.map((organization) => {
          return {
            id: organization?.organization_id,
            name: organization?.org_name,
          };
        });
        setOrganizations([
          { id: 'all', name: 'All Organizations' },
          ...filteredData,
        ]);
      } catch (err) {
        console.error('Failed to load organizations:', err);
        setOrgError('Failed to load organizations');
      } finally {
        setOrgLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  const applyFilters = () => {
    const [start, end] = dateRange;

    const newFilters = {
      ...(selectedRole !== 'all' && { userRole: selectedRole }),
      ...(selectedOrg !== 'all' && { organization: selectedOrg }),
      ...(start &&
        end && {
          dateRange: {
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0],
          },
        }),
    };

    setFilters(() => newFilters);
  };

  const resetFilters = () => {
    setDateRange([null, null]);
    setSelectedRole('all');
    setSelectedOrg('all');
    setFilters({});
  };

  const getFilterSummary = () => {
    const [start, end] = dateRange;
    const parts = [];

    if (start && end) {
      parts.push(
        `Date: ${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
      );
    }

    if (selectedRole !== 'all') {
      const roleName = baseRoles.find((r) => r.id === selectedRole)?.name;
      parts.push(`Role: ${roleName}`);
    }

    if (selectedOrg !== 'all') {
      const orgName = organizations.find((o) => o.id === selectedOrg)?.name;
      parts.push(`Organization: ${orgName}`);
    }

    return parts.length ? parts.join(' | ') : '';
  };
  return (
    <div className="innerpage detailed-page">
      <Container fluid>
        {/* Header */}
        <Row className="mb-3">
          <Col>
            <h4 className="fs-18 fw-semibold mb-0">
              Coverage Verification Analytics
            </h4>
            <p className="text-muted mb-4">
              Monitor verification performance and trends
            </p>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="mb-4">
          <Card.Header className="bg-white py-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <IconFilter className="me-2" size={18} />
                <span className="fw-medium">Filters</span>
                {(dateRange[0] ||
                  selectedRole !== 'all' ||
                  selectedOrg !== 'all') && (
                  <span className="ms-3 text-muted small">
                    {getFilterSummary()}
                  </span>
                )}
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={resetFilters}
                title="Reset Filters"
              >
                <IconRefresh size={16} className="me-1" />
                Reset
              </Button>
            </div>
          </Card.Header>

          <Card.Body className="pt-3 pb-3">
            <Form>
              <Row>
                {/* Date Range */}

                {/* Organization */}
                <Col md={4}>
                  <Form.Group className="mb-0">
                    <Form.Label>Organization</Form.Label>
                    {orgLoading ? (
                      <div className="text-muted small">Loading...</div>
                    ) : orgError ? (
                      <div className="text-danger small">{orgError}</div>
                    ) : (
                      <Form.Select
                        value={selectedOrg}
                        onChange={(e) => setSelectedOrg(e.target.value)}
                      >
                        {organizations.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Card.Body>

          <Card.Footer className="bg-white py-2">
            <div className="d-flex justify-content-end">
              <Button variant="outline-primary" onClick={applyFilters}>
                Apply Filters
              </Button>
            </div>
          </Card.Footer>
        </Card>

        {/* Summary Stats */}
        <Row className="mb-4">
          <VerificationStatsCards filters={filters} />
        </Row>

        {/* Top Verified Drugs & Active Providers */}
        <Row className="g-4 mb-4">
          <Col xs={12} lg={6}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex align-items-baseline">
                    <h5 className="fw-bold mb-0 me-2">Top Verified Drugs</h5>
                  </div>
                  <p className="text-muted small mb-0">
                    Most frequently checked medications
                  </p>
                </div>
                <TopVerifiedDrugs filters={filters} />
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} lg={6}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex align-items-baseline">
                    <h5 className="fw-bold mb-0 me-2">
                      Active Providers This Week
                    </h5>
                  </div>
                  <p className="text-muted small mb-0">
                    Top 5 provider engagement and verification metrics
                  </p>
                </div>
                <ActiveProvidersThisWeek filters={filters} />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Actively Queried Plans & State Distribution */}
        <Row className="g-4 mb-4">
          <Col xs={12} lg={6}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex align-items-baseline">
                    <h5 className="fw-bold mb-0 me-2">
                      Actively Queried Plans
                    </h5>
                    <small className="text-muted">(Top 5)</small>
                  </div>
                  <p className="text-muted small mb-0">
                    Distribution by insurance issuer
                  </p>
                </div>
                <ActivelyQueriedPlans filters={filters} />
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} lg={6}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body>
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3 gap-2">
                  <div className="mb-3">
                    <div className="d-flex align-items-baseline">
                      <h5 className="fw-bold mb-0 me-2">
                        Geographic Distribution
                      </h5>
                      <small className="text-muted">(Top 5)</small>
                    </div>
                    <p className="text-muted small mb-0">
                      Verifications by state
                    </p>
                  </div>
                </div>
                <VerificationsByState filters={filters} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CoverageVerificationModuleUsage;
